import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Star, Smile, Palette, Crown, Lock, PlayCircle } from "lucide-react";
import { useEffect, useState } from "react";
import api, { thumb } from "../lib/api";
import FeaturedCreators from "../components/FeaturedCreators";
import OpenStudioCountdown from "../components/OpenStudioCountdown";

export default function Home() {
  const [classes, setClasses] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/classes").then(({ data }) => setClasses(data.slice(0, 3))).catch(() => {});
    api.get("/tutorials").then(({ data }) => setTutorials(data.slice(0, 4))).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);
  const HERO = settings.hero_image;
  const STUDIO_IMG = settings.studio_image;
  const INSTRUCTOR_IMG = settings.founder_image;

  return (
    <div data-testid="home-page" className="fade-in bg-[#F2E9D8]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-[#121212]" />
              <span className="text-xs tracking-[0.3em] uppercase font-semibold">Lafayette's Creative Cake Studio</span>
            </div>
            <h1 data-testid="hero-title" className="font-serif font-black text-[#121212] leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              Cake classes, camps, parties &amp; <span className="lime-swipe italic">creative studio</span> experiences.
            </h1>
            <p data-testid="hero-subtitle" className="mt-10 text-lg text-[#121212]/80 max-w-xl leading-relaxed">
              Join us for hands-on cake decorating classes, kids camps, birthday parties,
              open studio cake kits, and online tutorials through the Cake Society Vault.
              Made for beginners, creators, and cake lovers of all ages.
            </p>
            <div className="mt-12 flex flex-wrap gap-4 items-center">
              <Link
                to="/classes"
                data-testid="hero-cta-classes"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all"
              >
                Book a Class <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/parties"
                data-testid="hero-cta-studio"
                className="inline-flex items-center gap-3 px-8 py-4 border-2 border-[#121212] text-[#121212] hover:bg-[#121212] hover:text-[#F2E9D8] text-xs tracking-[0.25em] uppercase font-bold transition-all"
              >
                Plan a Party
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-4 text-xs tracking-[0.3em] uppercase font-medium">
              <span>Learn</span><Star size={10} className="text-[#121212]" fill="#121212" />
              <span>Create</span><Star size={10} className="text-[#121212]" fill="#121212" />
              <span className="lime-swipe">Connect</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={HERO} alt="" className="w-full h-full object-cover grayscale-[20%]" />
            </div>
            <Star className="absolute -top-4 -left-4 text-[#121212] star-rotate" size={48} fill="#121212" />
            <div className="absolute -bottom-8 -right-4 bg-[#C6FF33] px-6 py-4 rotate-[-4deg]">
              <p className="font-serif italic text-2xl text-[#121212]">small classes</p>
              <p className="text-xs tracking-[0.2em] uppercase font-bold">big results</p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Studio launch countdown */}
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 -mt-4 mb-4">
        <OpenStudioCountdown />
      </div>

      {/* Marquee */}
      <div className="bg-[#121212] text-[#F2E9D8] py-5 overflow-hidden">
        <div className="marquee">
          <div className="marquee-track text-sm tracking-[0.3em] uppercase font-semibold">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex gap-12 px-6 items-center">
                <span>Cake Classes</span><Smile size={16} className="text-[#C6FF33]" />
                <span>Kids Camps</span><Smile size={16} className="text-[#C6FF33]" />
                <span className="text-[#C6FF33]">Birthday Parties</span><Smile size={16} className="text-[#C6FF33]" />
                <span>Open Studio</span><Smile size={16} className="text-[#C6FF33]" />
                <span>Cake Kits</span><Smile size={16} className="text-[#C6FF33]" />
                <span className="text-[#C6FF33]">Online Tutorials</span><Smile size={16} className="text-[#C6FF33]" />
                <span>Guest Creators</span><Smile size={16} className="text-[#C6FF33]" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Three pillars */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase font-bold mb-6">Ways to create with us</p>
            <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212] leading-[0.95]">
              Choose your <span className="lime-swipe">Cake Society</span> experience.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {[
              { num: "01", title: "Classes & Camps", desc: "Adult workshops, kids camps, and seasonal cake experiences.", to: "/classes" },
              { num: "02", title: "Birthday Parties", desc: "Celebrate with a hands-on cake decorating party made for kids, teens, or adults.", to: "/parties" },
              { num: "03", title: "Open Studio", desc: "Pick a cake kit, decorate at your own pace, and take it home.", to: "/open-studio" },
              { num: "04", title: "Cake Society Vault", desc: "Online tutorials for members, cake kit buyers, and at-home creators.", to: "/tutorials" },
            ].map((p, i) => (
              <Link
                key={i}
                to={p.to}
                data-testid={`pillar-${i}`}
                className="group flex items-start gap-6 p-8 bg-white border-2 border-[#121212] hover:bg-[#C6FF33] transition-all"
              >
                <span className="font-serif font-black text-4xl text-[#121212]/30 group-hover:text-[#121212]">{p.num}</span>
                <div className="flex-1">
                  <h3 className="font-serif font-black text-3xl text-[#121212]">{p.title}</h3>
                  <p className="mt-2 text-[#121212]/80">{p.desc}</p>
                </div>
                <ArrowRight className="text-[#121212] group-hover:translate-x-1 transition-transform mt-2" size={22} />
              </Link>
            ))}
            <Link
              to="/guest-creators"
              data-testid="creator-mondays-callout"
              className="group flex items-center justify-between gap-4 px-6 py-4 border-2 border-dashed border-[#121212]/40 hover:border-[#121212] hover:bg-[#F2E9D8] transition-all"
            >
              <p className="text-sm text-[#121212]/80">
                <span className="font-bold">Are you a local creator?</span> Apply to teach a Guest Creator workshop at Cake Society.
              </p>
              <ArrowRight className="text-[#121212] group-hover:translate-x-1 transition-transform shrink-0" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Membership */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 pb-8 lg:pb-12">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4 inline-flex items-center gap-2"><Crown size={14} /> One membership</p>
          <h2 className="font-serif font-black text-4xl lg:text-5xl text-[#121212]">
            Cake Society <span className="lime-swipe italic">Membership.</span>
          </h2>
          <p className="mt-5 text-lg text-[#121212]/80 leading-relaxed">
            For cake lovers who want more access, more perks, and more reasons to create all year.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Palette, title: "Monthly Open Studio Perk", desc: "Enjoy a monthly creative cake experience included with your membership." },
            { icon: PlayCircle, title: "Vault Access", desc: "Stream Cake Society tutorials and follow along with cake kit projects from home." },
            { icon: Sparkles, title: "Member Perks", desc: "Get special discounts, early access to classes and camps, and first dibs on new drops." },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} data-testid={`membership-perk-${i}`} className="p-8 bg-white border-2 border-[#121212] flex flex-col">
                <div className="w-12 h-12 flex items-center justify-center border-2 border-[#121212] bg-[#C6FF33]">
                  <Icon size={22} className="text-[#121212]" />
                </div>
                <h3 className="font-serif font-black text-2xl text-[#121212] mt-5 leading-tight">{c.title}</h3>
                <p className="mt-3 text-[#121212]/80 leading-relaxed">{c.desc}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-[#121212]/70 italic">
          Perfect for kids, adults, families, and anyone who wants cake to be part of their routine.
        </p>
        <div className="mt-8 flex justify-center">
          <Link
            to="/membership"
            data-testid="membership-cta"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all"
          >
            Explore Membership <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* Upcoming classes */}
      {classes.length > 0 && (
      <section className="bg-[#A89D8A]/15 py-24 lg:py-32 border-y border-[#121212]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase font-bold mb-3">Coming up</p>
              <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212]">
                Upcoming <span className="lime-swipe italic">workshops</span>
              </h2>
            </div>
            <Link to="/classes" className="text-xs tracking-[0.3em] uppercase font-bold hover:gap-3 inline-flex items-center gap-2 transition-all">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {classes.map((c) => (
              <Link
                key={c.id}
                to={`/classes/${c.slug || c.id}`}
                data-testid={`home-class-${c.id}`}
                className="group bg-white border-2 border-[#121212] hover:-translate-y-1 transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden border-b-2 border-[#121212]">
                  <img src={c.image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="text-xs tracking-[0.3em] uppercase font-bold">{c.date}</p>
                  <h3 className="font-serif font-black text-2xl text-[#121212] mt-3 leading-tight">{c.title}</h3>
                  <div className="mt-5 pt-5 border-t border-[#121212]/10 flex justify-between items-baseline">
                    <span className="text-[#121212]/60 text-xs tracking-wider uppercase">{c.duration}</span>
                    <span className="font-serif font-black text-3xl">${c.price}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Featured creator */}
      <FeaturedCreators />

      {/* Vault teaser */}
      <section data-testid="home-vault-teaser" className="bg-[#121212] text-[#F2E9D8] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="text-xs tracking-[0.3em] uppercase font-bold mb-3 text-[#C6FF33]">Members-only digital library</p>
              <h2 className="font-serif font-black text-5xl lg:text-6xl leading-[0.95]">
                Inside the <span className="text-[#C6FF33] italic">Vault.</span>
              </h2>
              <p className="mt-6 text-[#F2E9D8]/75 text-lg leading-relaxed">
                A growing library of decorating tutorials, techniques, and creative projects —
                streamed anytime. Become a member to unlock it all.
              </p>
            </div>
            <Link to="/membership" className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33] hover:gap-3 inline-flex items-center gap-2 transition-all">
              Join to unlock <ArrowRight size={16} />
            </Link>
          </div>
          {tutorials.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {tutorials.map((t) => (
              <Link
                key={t.id}
                to={`/tutorials/${t.id}`}
                data-testid={`vault-teaser-${t.id}`}
                className="group relative aspect-[4/5] overflow-hidden border-2 border-[#F2E9D8]/20 hover:border-[#C6FF33] transition-all"
              >
                <img src={thumb(t.image_url, 500)} alt={t.title} className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/40 to-transparent" />
                <div className="absolute top-3 right-3 w-9 h-9 bg-[#121212]/70 backdrop-blur border border-[#F2E9D8]/30 flex items-center justify-center">
                  <Lock size={15} className="text-[#F2E9D8]" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#C6FF33]">{t.level}</p>
                  <h3 className="font-serif font-black text-lg leading-tight mt-1">{t.title}</h3>
                </div>
              </Link>
            ))}
          </div>
          )}
          <Link
            to="/membership"
            data-testid="vault-teaser-cta"
            className="mt-10 inline-flex items-center gap-3 px-8 py-4 bg-[#C6FF33] text-[#121212] hover:bg-[#b6ef23] text-xs tracking-[0.25em] uppercase font-bold transition-all"
          >
            <PlayCircle size={16} /> Join to unlock the full library
          </Link>
        </div>
      </section>

      {/* Studio rental section */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 items-center">
        <div className="relative">
          <div className="aspect-[4/5] overflow-hidden border-2 border-[#121212]">
            <img src={STUDIO_IMG} alt="Studio" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -top-6 -right-4 bg-[#FF8F80] px-5 py-3 rotate-3 border-2 border-[#121212]">
            <p className="text-xs tracking-[0.2em] uppercase font-bold">Now booking</p>
          </div>
        </div>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase font-bold mb-6">For creators</p>
          <h2 className="font-serif font-black text-5xl lg:text-6xl text-[#121212] leading-[0.95]">
            Bring your <span className="lime-swipe italic">community</span> to Cake Society.
          </h2>
          <p className="mt-8 text-lg text-[#121212]/80 leading-relaxed">
            Host your class, workshop, or creative experience in a flexible studio designed for a variety of skills and project types.
          </p>
          <p className="mt-4 text-lg text-[#121212]/80 leading-relaxed">
            The space includes six tables and can accommodate up to 12 guests for moderate-sized projects or up to 24 guests for smaller projects. Class times are flexible, with evening sessions recommended for the strongest guest availability.
          </p>
          <p className="mt-4 text-lg text-[#121212]/80 leading-relaxed">
            Each instructor is responsible for providing all tools, materials, and supplies needed for their class. Cake Society will manage registration, booking, and promotion.
          </p>
          <ul className="mt-10 space-y-3">
            {["Flexible space for a variety of creative skills", "Six studio tables", "Up to 12 guests for moderate-sized projects", "Up to 24 guests for smaller projects", "Flexible scheduling, with evenings recommended", "Cake Society manages booking and promotion", "Instructor provides all class tools and supplies"].map((f) => (
              <li key={f} className="flex items-center gap-4 text-[#121212]">
                <Sparkles size={16} className="text-[#C6FF33]" fill="#C6FF33" /> {f}
              </li>
            ))}
          </ul>
          <Link
            to="/studio"
            data-testid="studio-cta"
            className="mt-12 inline-flex items-center gap-3 px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all"
          >
            Apply to Teach <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* About */}
      <section className="bg-[#121212] text-[#F2E9D8] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-xs tracking-[0.3em] uppercase font-bold mb-6 text-[#C6FF33]">Meet the founder</p>
            <h2 className="font-serif font-black text-5xl lg:text-6xl leading-[0.95]">
              Hi, I'm {settings.founder_name || "Elena"}. <em className="font-medium">Welcome.</em>
            </h2>
            <p className="mt-8 text-lg text-[#F2E9D8]/80 leading-relaxed">
              {settings.founder_bio || "I've been decorating cakes for fifteen years..."}
            </p>
            <p className="mt-6 text-xs tracking-[0.4em] uppercase font-bold">
              <span>We teach</span> · <span>We create</span> · <span className="text-[#C6FF33]">We connect</span>
            </p>
          </div>
          <div className="order-1 lg:order-2 relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={INSTRUCTOR_IMG} alt="Founder" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#C6FF33] px-6 py-4 rotate-[-3deg]">
              <p className="font-serif font-black italic text-2xl text-[#121212]">don't rush me</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
